/**
 * STREAK COUNTER
 *
 * Displays user's current streak with animated flame icon
 * Shows streak milestones and provides visual feedback
 */

import React from 'react';
import { Flame, Calendar, Award, Shield } from 'lucide-react';

interface StreakCounterProps {
  currentStreak: number;
  longestStreak: number;
  freezeTokens: number;
  lastActivityDate?: Date;
  compact?: boolean;
  className?: string;
}

const StreakCounter: React.FC<StreakCounterProps> = ({
  currentStreak,
  longestStreak,
  freezeTokens,
  lastActivityDate,
  compact = false,
  className = ''
}) => {
  // Calculate flame intensity based on streak
  const getFlameIntensity = (streak: number): string => {
    if (streak >= 100) return 'text-purple-500 drop-shadow-[0_0_15px_rgba(168,85,247,0.7)]';
    if (streak >= 30) return 'text-orange-500 drop-shadow-[0_0_12px_rgba(249,115,22,0.7)]';
    if (streak >= 7) return 'text-red-500 drop-shadow-[0_0_10px_rgba(239,68,68,0.7)]';
    if (streak >= 3) return 'text-yellow-500 drop-shadow-[0_0_8px_rgba(234,179,8,0.7)]';
    return 'text-gray-400';
  };

  const getStreakMessage = (streak: number): string => {
    if (streak === 0) return '🚀 Start your streak today!';
    if (streak === 1) return '✨ Great start! Keep it going!';
    if (streak < 7) return '💪 Building momentum!';
    if (streak < 14) return '🔥 On fire! Keep burning!';
    if (streak < 30) return '⚡ Unstoppable force!';
    if (streak < 60) return '🏆 Consistent champion!';
    if (streak < 100) return '💎 Incredible dedication!';
    return '👑 LEGENDARY STREAK!';
  };

  const getNextMilestone = (streak: number): { milestone: number; name: string } | null => {
    const milestones = [
      { milestone: 7, name: '7-Day Warrior' },
      { milestone: 14, name: '14-Day Champion' },
      { milestone: 30, name: '30-Day Master' },
      { milestone: 60, name: '60-Day Legend' },
      { milestone: 100, name: '100-Day Titan' },
      { milestone: 365, name: '365-Day God' }
    ];

    return milestones.find(m => streak < m.milestone) || null;
  };

  const nextMilestone = getNextMilestone(currentStreak);
  const progressToNextMilestone = nextMilestone
    ? ((currentStreak / nextMilestone.milestone) * 100).toFixed(0)
    : 100;

  const flameIntensity = getFlameIntensity(currentStreak);

  if (compact) {
    return (
      <div className={`inline-flex items-center space-x-2 ${className}`}>
        <Flame className={`w-5 h-5 ${flameIntensity} ${currentStreak > 0 ? 'animate-pulse' : ''}`} />
        <span className="text-lg font-bold text-gray-900">{currentStreak}</span>
        <span className="text-sm text-gray-600">day streak</span>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-gray-900">Your Streak</h3>
        {freezeTokens > 0 && (
          <div className="flex items-center space-x-1 px-3 py-1 bg-blue-100 rounded-full">
            <Shield className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-900">
              {freezeTokens} {freezeTokens === 1 ? 'Freeze' : 'Freezes'}
            </span>
          </div>
        )}
      </div>

      {/* Main Streak Display */}
      <div className="text-center mb-6">
        <div className="relative inline-block">
          {/* Flame Icon */}
          <Flame
            className={`w-24 h-24 mx-auto mb-4 ${flameIntensity} ${
              currentStreak > 0 ? 'animate-flame-flicker' : ''
            }`}
          />
          {/* Streak Number Overlay */}
          <div className="absolute inset-0 flex items-center justify-center pt-2">
            <span className="text-4xl font-black text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
              {currentStreak}
            </span>
          </div>
        </div>
        <p className="text-2xl font-bold text-gray-900 mb-2">
          {currentStreak === 0 ? 'No streak yet' : `${currentStreak} Day${currentStreak !== 1 ? 's' : ''}`}
        </p>
        <p className="text-sm text-gray-600">{getStreakMessage(currentStreak)}</p>
      </div>

      {/* Next Milestone Progress */}
      {nextMilestone && currentStreak > 0 && (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold text-gray-700">Next Milestone</span>
            <span className="text-sm text-gray-600">
              {nextMilestone.milestone - currentStreak} days to {nextMilestone.name}
            </span>
          </div>
          <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-orange-400 to-red-600 transition-all duration-500"
              style={{ width: `${progressToNextMilestone}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shine" />
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Award className="w-4 h-4 text-red-600" />
            <span className="text-xs font-semibold text-red-900">Longest Streak</span>
          </div>
          <div className="text-2xl font-bold text-red-900">{longestStreak}</div>
          <div className="text-xs text-red-700">days</div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Calendar className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-semibold text-blue-900">Last Activity</span>
          </div>
          <div className="text-sm font-bold text-blue-900">
            {lastActivityDate
              ? lastActivityDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              : 'Today'}
          </div>
          <div className="text-xs text-blue-700">
            {lastActivityDate
              ? new Date().toDateString() === lastActivityDate.toDateString()
                ? 'Today'
                : `${Math.floor((new Date().getTime() - lastActivityDate.getTime()) / (1000 * 60 * 60 * 24))} days ago`
              : 'Just now'}
          </div>
        </div>
      </div>

      {/* Streak Protection Info */}
      {currentStreak > 0 && (
        <div className="mt-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs text-gray-700">
            {freezeTokens > 0 ? (
              <>
                💡 <span className="font-semibold">Tip:</span> You have {freezeTokens} streak{' '}
                {freezeTokens === 1 ? 'freeze' : 'freezes'} to protect your streak if you miss a day!
              </>
            ) : (
              <>
                ⚠️ <span className="font-semibold">Warning:</span> You have no streak freezes left.
                Don't miss tomorrow or your streak will reset!
              </>
            )}
          </p>
        </div>
      )}

      <style>{`
        @keyframes flame-flicker {
          0%, 100% {
            transform: scale(1) rotate(0deg);
          }
          25% {
            transform: scale(1.05) rotate(-2deg);
          }
          50% {
            transform: scale(0.95) rotate(2deg);
          }
          75% {
            transform: scale(1.02) rotate(-1deg);
          }
        }

        .animate-flame-flicker {
          animation: flame-flicker 2s ease-in-out infinite;
        }

        @keyframes shine {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .animate-shine {
          animation: shine 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default StreakCounter;
